import React, { useState, useLayoutEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import FadeInSection from './FadeInSection';
import CustomSelect from './CustomSelect';
import '../styles/MortgageCalculator.scss';

const TERM_OPTIONS = [
  { label: '30-year fixed', value: '30' },
  { label: '20-year fixed', value: '20' },
  { label: '15-year fixed', value: '15' },
  { label: '10-year fixed', value: '10' },
];

const COUNTY_TAX_RATES = [
  { label: 'San Francisco County', value: 'San Francisco', rate: '1.18' },
  { label: 'San Mateo County', value: 'San Mateo', rate: '1.17' },
  { label: 'Santa Clara County', value: 'Santa Clara', rate: '1.20' },
  { label: 'Alameda County', value: 'Alameda', rate: '1.25' },
  { label: 'Contra Costa County', value: 'Contra Costa', rate: '1.25' },
  { label: 'Marin County', value: 'Marin', rate: '1.10' },
  { label: 'Sonoma County', value: 'Sonoma', rate: '1.15' },
  { label: 'Napa County', value: 'Napa', rate: '1.28' },
  { label: 'Solano County', value: 'Solano', rate: '1.25' },
];

const fmt = (n) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const fmtDecimal = (n) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

function calculate({ homePrice, downPct, rate, termYears, taxRate, insuranceRate, hoa }) {
  const price = parseFloat(homePrice) || 0;
  const down = price * (parseFloat(downPct) / 100);
  const loan = price - down;
  const monthlyRate = parseFloat(rate) / 100 / 12;
  const n = parseFloat(termYears) * 12;

  let pi = 0;
  if (loan > 0 && monthlyRate > 0) {
    pi = loan * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
  } else if (loan > 0) {
    pi = loan / n;
  }

  const monthlyTax = (price * (parseFloat(taxRate) / 100)) / 12;
  const monthlyInsurance = (price * (parseFloat(insuranceRate) / 100)) / 12;
  const monthlyHoa = parseFloat(hoa) || 0;
  const pmi = parseFloat(downPct) < 20 ? (loan * 0.008) / 12 : 0;

  const total = pi + monthlyTax + monthlyInsurance + monthlyHoa + pmi;

  return {
    pi, monthlyTax, monthlyInsurance, monthlyHoa, pmi, total,
    loan, down,
  };
}

export default function MortgageCalculator() {
  const [homePrice, setHomePrice] = useState('1200000');
  const [downPct, setDownPct] = useState('20');
  const [downMode, setDownMode] = useState('pct'); // 'pct' | 'dollar'
  const [rate, setRate] = useState('6.75');
  const [termYears, setTermYears] = useState('30');
  const [county, setCounty] = useState('San Francisco');
  const [taxRate, setTaxRate] = useState('1.18');
  const [insuranceRate, setInsuranceRate] = useState('0.35');
  const [hoa, setHoa] = useState('');

  const calculatorTracked = useRef(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const price = parseFloat(homePrice) || 0;
  const results = calculate({ homePrice, downPct, rate, termYears, taxRate, insuranceRate, hoa });

  const handlePriceInput = useCallback((e) => {
    setHomePrice(e.target.value.replace(/[^0-9]/g, ''));
    if (!calculatorTracked.current && typeof window.gtag === 'function') {
      calculatorTracked.current = true;
      window.gtag('event', 'mortgage_calculator_used');
    }
  }, []);

  const handleCountyChange = (val) => {
    const selected = COUNTY_TAX_RATES.find((c) => c.value === val);
    setCounty(val);
    if (selected) setTaxRate(selected.rate);
  };

  const downAmount = price * (parseFloat(downPct) / 100);

  const handleDownChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    if (downMode === 'pct') {
      setDownPct(val);
    } else {
      const dollar = parseFloat(val) || 0;
      const pct = price > 0 ? ((dollar / price) * 100).toFixed(2) : '0';
      setDownPct(pct);
    }
  };

  const handleDownModeSwitch = (mode) => {
    setDownMode(mode);
  };

  const breakdown = [
    { label: 'Principal & Interest', value: results.pi, key: 'pi' },
    { label: 'Property Tax', value: results.monthlyTax, key: 'tax' },
    { label: 'Home Insurance', value: results.monthlyInsurance, key: 'ins' },
    ...(results.pmi > 0 ? [{ label: 'PMI', value: results.pmi, key: 'pmi' }] : []),
    ...(results.monthlyHoa > 0 ? [{ label: 'HOA', value: results.monthlyHoa, key: 'hoa' }] : []),
  ];

  return (
    <div className="mc-page">
      <Helmet>
        <title>Mortgage Calculator | Lily Lai Real Estate</title>
        <meta name="description" content="Estimate your monthly mortgage payment for a Bay Area home purchase. Includes property tax by county, home insurance, HOA, and PMI." />
        <link rel="canonical" href="https://www.lilylaisf.com/mortgage-calculator" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lilylaisf.com/mortgage-calculator" />
        <meta property="og:title" content="Bay Area Mortgage Calculator | Lily Lai Real Estate" />
        <meta property="og:description" content="Estimate your monthly mortgage payment with Bay Area county property tax rates built in. Includes PMI, HOA, and insurance." />
        <meta property="og:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bay Area Mortgage Calculator | Lily Lai Real Estate" />
        <meta name="twitter:description" content="Estimate your monthly mortgage payment with Bay Area county property tax rates built in. Includes PMI, HOA, and insurance." />
        <meta name="twitter:image" content="https://res.cloudinary.com/dlaituqwq/image/upload/f_auto,q_auto/wr8plprsi0qxxs3lhmxr" />
      </Helmet>

      <div className="mc-header">
        <FadeInSection>
          <p className="mc-eyebrow">Buyer Resources</p>
          <h1 className="mc-title">Mortgage Calculator</h1>
          <p className="mc-subtitle">Estimate your monthly payment for a Bay Area home purchase.</p>
        </FadeInSection>
      </div>

      <div className="mc-body">
        {/* ── Inputs ── */}
        <FadeInSection>
          <div className="mc-card mc-inputs">
            <div className="mc-field">
              <label htmlFor="mc-price">Home Price</label>
              <div className="mc-input-prefix">
                <span>$</span>
                <input
                  id="mc-price"
                  type="text"
                  inputMode="numeric"
                  value={Number(homePrice).toLocaleString('en-US')}
                  onChange={handlePriceInput}
                />
              </div>
            </div>

            <div className="mc-field">
              <div className="mc-label-row">
                <label htmlFor="mc-down">Down Payment</label>
                <div className="mc-toggle" role="group" aria-label="Down payment unit">
                  <button
                    type="button"
                    className={`mc-toggle-btn${downMode === 'pct' ? ' active' : ''}`}
                    onClick={() => handleDownModeSwitch('pct')}
                  >%</button>
                  <button
                    type="button"
                    className={`mc-toggle-btn${downMode === 'dollar' ? ' active' : ''}`}
                    onClick={() => handleDownModeSwitch('dollar')}
                  >$</button>
                </div>
              </div>
              {downMode === 'pct' ? (
                <div className="mc-input-suffix">
                  <input
                    id="mc-down"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="100"
                    step="0.5"
                    value={downPct}
                    onChange={handleDownChange}
                  />
                  <span>%</span>
                </div>
              ) : (
                <div className="mc-input-prefix">
                  <span>$</span>
                  <input
                    id="mc-down"
                    type="text"
                    inputMode="numeric"
                    value={Math.round(downAmount).toLocaleString('en-US')}
                    onChange={handleDownChange}
                  />
                </div>
              )}
              <div className="mc-down-sub">
                {downMode === 'pct'
                  ? <span>{fmt(downAmount)}</span>
                  : <span>{parseFloat(downPct).toFixed(1)}% of purchase price</span>
                }
              </div>
              <input
                type="range"
                min="0"
                max="50"
                step="0.5"
                value={parseFloat(downPct) || 0}
                onChange={(e) => setDownPct(e.target.value)}
                className="mc-slider"
                aria-label="Down payment percentage"
              />
              {parseFloat(downPct) < 20 && (
                <p className="mc-hint">PMI typically applies when down payment is below 20%.</p>
              )}
            </div>

            <div className="mc-row-2">
              <div className="mc-field">
                <label htmlFor="mc-rate">Interest Rate</label>
                <div className="mc-input-suffix">
                  <input
                    id="mc-rate"
                    type="number"
                    min="0"
                    max="20"
                    step="0.05"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                  <span>%</span>
                </div>
              </div>

              <div className="mc-field">
                <label htmlFor="mc-term">Loan Term</label>
                <CustomSelect
                  id="mc-term"
                  value={termYears}
                  onChange={(val) => setTermYears(val)}
                  options={TERM_OPTIONS}
                />
              </div>
            </div>

            <div className="mc-divider" />

            <p className="mc-section-label">Bay Area Estimates</p>

            <div className="mc-field">
              <label htmlFor="mc-county">County</label>
              <CustomSelect
                id="mc-county"
                value={county}
                onChange={handleCountyChange}
                options={COUNTY_TAX_RATES}
              />
            </div>

            <div className="mc-row-2">
              <div className="mc-field">
                <label htmlFor="mc-tax">Property Tax Rate</label>
                <div className="mc-input-suffix">
                  <input
                    id="mc-tax"
                    type="number"
                    min="0"
                    max="5"
                    step="0.01"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                  />
                  <span>%</span>
                </div>
                <p className="mc-field-hint">Auto-filled from county — edit to customize</p>
              </div>

              <div className="mc-field">
                <label htmlFor="mc-ins">Home Insurance Rate</label>
                <div className="mc-input-suffix">
                  <input
                    id="mc-ins"
                    type="number"
                    min="0"
                    max="5"
                    step="0.01"
                    value={insuranceRate}
                    onChange={(e) => setInsuranceRate(e.target.value)}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            <div className="mc-field">
              <label htmlFor="mc-hoa">Monthly HOA <span className="mc-optional">(optional)</span></label>
              <div className="mc-input-prefix">
                <span>$</span>
                <input
                  id="mc-hoa"
                  type="number"
                  min="0"
                  step="50"
                  placeholder="0"
                  value={hoa}
                  onChange={(e) => setHoa(e.target.value)}
                />
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* ── Result ── */}
        <FadeInSection>
          <div className="mc-card mc-result">
            <p className="mc-result-label">Est. Monthly Payment</p>
            <p className="mc-result-total">{fmtDecimal(results.total)}</p>
            <p className="mc-result-loan-note">
              {fmt(results.loan)} loan · {termYears}-yr fixed · {rate}% APR
            </p>
            <p className="mc-result-county">{county} County · {taxRate}% tax</p>

            <div className="mc-breakdown">
              {breakdown.map((item) => (
                <div key={item.key} className="mc-breakdown-row">
                  <span className="mc-breakdown-label">{item.label}</span>
                  <span className="mc-breakdown-value">{fmtDecimal(item.value)}</span>
                </div>
              ))}
              <div className="mc-breakdown-total-row">
                <span>Total / month</span>
                <span>{fmtDecimal(results.total)}</span>
              </div>
            </div>

            <div className="mc-down-summary">
              <div className="mc-down-row">
                <span>Down Payment</span>
                <span>{fmt(results.down)} ({downPct}%)</span>
              </div>
              <div className="mc-down-row">
                <span>Loan Amount</span>
                <span>{fmt(results.loan)}</span>
              </div>
            </div>

            <p className="mc-disclaimer">
              This estimate is for illustrative purposes only and does not constitute a loan offer.
              Actual rates, taxes, and insurance vary. Contact a licensed lender for a formal quote.
            </p>

            <a href="/contact" className="mc-cta">Talk to Lily About Buying</a>
          </div>
        </FadeInSection>
      </div>
    </div>
  );
}
